import Image from 'next/image'
import Link from 'next/link'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import LPContainer from '~/components/LandingPage/LPContainer'
import { FCC } from '~/core/@types/global'
import { ICONS_SOCIAL } from '~/core/constants/social'
import {
  ABOUT_US_URL,
  ATTRACT_SOURCE_URL,
  FACEBOOK_URL,
  FEATURES_URL,
  HIRE_ANALYTICS_URL,
  LINKEDIN_URL,
  PRIVACY_POLICY_URL,
  SOLUTIONS_ENTERPRISE_URL,
  SOLUTIONS_RECRUITMENT_AGENCY_URL,
  TALENT_AI_URL,
  TALENT_INTELLIGENCE_URL,
  TERM_OF_SERVICES_URL,
  TRACK_COLLABORATE_URL,
  URL_BLOG,
  URL_HELP_CENTER,
  X_URL
} from '~/core/constants/url'
import { cn } from '~/core/ui/utils'

const FooterTitle: FCC<{ className?: string }> = ({
  children,
  className = ''
}) => {
  return (
    <div
      className={`text-lg tablet:text-xl font-normal text-gray-300 ${className}`}>
      {children}
    </div>
  )
}

const FooterLink: FCC<{
  href: string
  className?: string
  target?: string
}> = ({ href, children, className = '', target }) => {
  return (
    <Link
      href={href}
      target={target}
      className={`block text-base tablet:text-lg hover:underline ${className} max-w-max`}>
      {children}
    </Link>
  )
}

const FooterLandingPage: FC = () => {
  const { t } = useTranslation()
  return (
    <div className="bg-[#1D1E27] text-gray-400">
      <LPContainer>
        <div className="grid grid-cols-[172px_1fr] gap-x-[42px] gap-y-20 pb-10 pt-20 tablet:grid-cols-2 tablet:justify-between tablet:gap-x-[207px] tablet:pb-16 desktop:flex desktop:justify-between desktop:justify-items-stretch desktop:gap-0">
          <div className="">
            <FooterTitle className="mb-4">
              {t('lp:footer:solutions')}
            </FooterTitle>
            <FooterLink href={SOLUTIONS_ENTERPRISE_URL} className="mb-4">
              {t('lp:footer:enterprise')}
            </FooterLink>
            <FooterLink
              href={SOLUTIONS_RECRUITMENT_AGENCY_URL}
              className="mb-4">
              {t('lp:footer:recruitment_agency')}
            </FooterLink>
          </div>

          <div className="">
            <FooterTitle className="mb-4">{t('lp:footer:product')}</FooterTitle>
            <FooterLink
              href={TALENT_INTELLIGENCE_URL}
              target="_blank"
              className="mb-4">
              {t('lp:footer:talent_platform')}
            </FooterLink>
            <ul>
              <li className="pl-6 relative">
                <div className="p-0 w-1.5 h-1.5 bg-gray-400 rounded-md absolute left-1.5 top-2.5" />
                <FooterLink
                  href={TALENT_AI_URL}
                  target="_blank"
                  className="mb-4">
                  {t('lp:footer:talent_ai')}
                </FooterLink>
              </li>
            </ul>
            <FooterLink href={FEATURES_URL} target="_blank" className="mb-4">
              {t('lp:footer:ats')}
            </FooterLink>
            <ul>
              <li className="pl-6 relative">
                <div className="p-0 w-1.5 h-1.5 bg-gray-400 rounded-md absolute left-1.5 top-2.5" />
                <FooterLink
                  href={ATTRACT_SOURCE_URL}
                  target="_blank"
                  className="mb-4">
                  {t('lp:footer:attract_source')}
                </FooterLink>
              </li>
              <li className="pl-6 relative">
                <div className="p-0 w-1.5 h-1.5 bg-gray-400 rounded-md absolute left-1.5 top-2.5" />
                <FooterLink
                  href={TRACK_COLLABORATE_URL}
                  target="_blank"
                  className="mb-4">
                  {t('lp:footer:track_collaboration')}
                </FooterLink>
              </li>
              <li className="pl-6 relative">
                <div className="p-0 w-1.5 h-1.5 bg-gray-400 rounded-md absolute left-1.5 top-2.5" />
                <FooterLink
                  href={HIRE_ANALYTICS_URL}
                  target="_blank"
                  className="mb-4">
                  {t('lp:footer:hire_analytics')}
                </FooterLink>
              </li>
            </ul>
          </div>

          <div className="">
            <FooterTitle className="mb-4">
              {t('lp:footer:resources')}
            </FooterTitle>
            <FooterLink href={URL_BLOG} className="mb-4">
              {t('lp:footer:blog')}
            </FooterLink>
            <FooterLink href="/llms.html" className="mb-4">
              llms.txt
            </FooterLink>
            {/* <FooterLink
              href={INTERVIEW_QUESTION_URL}
              target="_blank"
              className="mb-4">
              {t('lp:footer:interview_question_template')}
            </FooterLink>
            <FooterLink
              href={JOB_DESCRIPTIONS_URL}
              target="_blank"
              className="mb-4">
              {t('lp:footer:job_description')}
            </FooterLink>
            <FooterLink
              href={HR_OKR_EBOOK_URL}
              target="_blank"
              className="mb-4">
              {t('lp:footer:hr-okr-ebook')}
            </FooterLink> */}
          </div>

          <div className="">
            <FooterTitle className="mb-4">{t('lp:footer:company')}</FooterTitle>
            <FooterLink href={ABOUT_US_URL} className="mb-4">
              {t('lp:footer:about_us')}
            </FooterLink>
            <FooterLink href={URL_HELP_CENTER} className="mb-4">
              {t('lp:footer:help_center')}
            </FooterLink>
            <FooterLink href={TERM_OF_SERVICES_URL} className="mb-4">
              {t('lp:footer:terms_of_service')}
            </FooterLink>
            <FooterLink href={PRIVACY_POLICY_URL} className="mb-4">
              {t('lp:footer:privacy_policy')}
            </FooterLink>
          </div>

          <div className="">
            <FooterTitle className="mb-4">
              {t('lp:footer:get_in_touch')}
            </FooterTitle>
            <div className="mb-[60px] flex items-center space-x-5">
              <Link href={FACEBOOK_URL} target="_blank">
                <Image
                  src={ICONS_SOCIAL.facebook_darkmode}
                  loading="lazy"
                  width={0}
                  height={0}
                  className="w-[20px] h-auto"
                  alt="Facebook icon"
                />
              </Link>
              <Link href={LINKEDIN_URL} target="_blank">
                <Image
                  src={ICONS_SOCIAL.linkedin_darkmode}
                  loading="lazy"
                  width={0}
                  height={0}
                  className="w-[20px] h-auto"
                  alt="Linkedin icon"
                />
              </Link>
              <Link href={X_URL} target="_blank">
                <Image
                  src={ICONS_SOCIAL.twitter_darkmode}
                  loading="lazy"
                  width={0}
                  height={0}
                  className="w-[20px] h-auto"
                  alt="Twitter icon"
                />
              </Link>
            </div>
          </div>
        </div>
      </LPContainer>
      <div className="h-px bg-gray-700" />
      <LPContainer>
        <div
          className={cn(
            'flex flex-col gap-4',
            'tablet:flex-row tablet:items-center tablet:justify-between py-8'
          )}>
          <img
            src="/img/logo/talentsforce-logo.svg"
            alt="logo"
            className="w-[160px]"
          />
          <div className="text-base text-gray-300">
            {t('lp:footer:copy_right')}
          </div>
          {/* <LanguageTranslation /> */}
        </div>
      </LPContainer>
    </div>
  )
}

export default FooterLandingPage
